import {useMemo} from 'react';
import {ProductRecommendationsType, TGetRecommendationsPropsType} from 'modules/recommendations/types';
import {useGetProductRecommendationsQuery, useGetRecommendationsQuery} from 'modules/recommendations/services';
import {NEXT_PUBLIC_MASTER_HOST} from 'core/helpers/env';
import {usePathname} from 'next/navigation';
import {useAppSelector} from 'core/redux/hooks/useAppSelector';
import {selectProductData} from 'modules/product-card/selectors';
import {IProductShort} from 'modules/products/types';
import {useIsProductFineAndRare} from 'modules/product-card/hooks/useIsProductFineAndRare';
import {useRedirectActive} from 'modules/product-card/hooks/useRedirectActive';

const RECOMMENDATION_SELECTORS = [
    ProductRecommendationsType.WebOtherItemsOfTheBrandPdp,
    ProductRecommendationsType.WebPdpPurchasedTogether,
];

export const useProductPageRecommendationsState = () => {
    const pathname = usePathname();
    const isRedirectActive = useRedirectActive();

    const productData = useAppSelector(selectProductData);
    const isFineAndRareCategory = Boolean(useIsProductFineAndRare());

    const {data: productRecommendationsResponse} = useGetProductRecommendationsQuery(
        {
            data: {
                filter: {
                    id: productData?.id ?? '',
                    location: `${NEXT_PUBLIC_MASTER_HOST}${pathname}`,
                    selector: RECOMMENDATION_SELECTORS,
                },
            },
        },
        {
            skip: !productData || isFineAndRareCategory || isRedirectActive,
        },
    );

    const {data: recommendationsResponse} = useGetRecommendationsQuery(
        {
            data: [
                {
                    type: 'personal',
                    filter: {
                        staticUnifierItemNo: productData?.staticUnifier,
                        article: productData?.id,
                    },
                },
                {
                    type: 'similar_in_flavor',
                    filter: {
                        staticUnifierItemNo: productData?.staticUnifier,
                        article: productData?.id,
                    },
                },
                {
                    type: 'recently_viewed',
                    filter: {
                        staticUnifierItemNo: productData?.staticUnifier,
                        article: productData?.id,
                    },
                },
                {
                    type: 'popular_rcs',
                    pagination: {
                        page: 1,
                        limit: 20,
                    },
                    filter: {
                        article: productData?.id,
                    },
                },
            ],
            location: {
                page: 'pdp',
                source: 'web',
            },
        },
        {
            skip: !productData?.id || !productData?.staticUnifier || isFineAndRareCategory || isRedirectActive,
        },
    );

    const recommendationsData = useMemo(() => {
        if (isFineAndRareCategory) {
            return {};
        }

        return {
            ...(productRecommendationsResponse?.data?.items ?? []).reduce<
                Partial<
                    Record<
                        ProductRecommendationsType,
                        {
                            items: IProductShort[];
                            title: string;
                        }
                    >
                >
            >((acc, {selector, items, title}) => {
                acc[selector as ProductRecommendationsType] = {items, title};
                return acc;
            }, {}),
            ...(recommendationsResponse?.data?.items ?? []).reduce<
                Partial<
                    Record<
                        TGetRecommendationsPropsType,
                        {
                            items: IProductShort[];
                            title: string;
                        }
                    >
                >
            >((acc, {type, items, title}) => {
                acc[type as TGetRecommendationsPropsType] = {items, title};
                return acc;
            }, {}),
        };
    }, [isFineAndRareCategory, productRecommendationsResponse?.data?.items, recommendationsResponse?.data?.items]);
    return {recommendationsData};
};

export default useProductPageRecommendationsState;
